import { useState, useEffect, useContext } from 'react';
import Form from 'react-bootstrap/Form';
import { Col, Table, Badge, Row } from 'react-bootstrap';
import { getReceivableSummary } from '../../../../usecases/accounts-management-usecase';
import { roleContext } from '../../../../context/role/context';
import { formatMoney } from '../../../../utils/formating';
import { dispatchCustomEvent } from '../../../../utils/dispatch';
import { RotateCw } from 'lucide-react';
import '../style.css';

/**
 * Componente selector de cuentas por cobrar mejorado.
 * Permite buscar clientes y seleccionar una cuenta específica para abonar.
 * Muestra detalles completos de las cuentas agrupadas por tercero.
 * 
 * @param {Object} props
 * @param {number} props.selectedAccountId - ID de la cuenta seleccionada
 * @param {Function} props.onSelect - Callback cuando se selecciona una cuenta
 * @param {string} props.error - Mensaje de error de validación
 */
function ReceivableAccountSelector({ selectedAccountId, onSelect, error }) {
    const { profile } = useContext(roleContext);
    const [accountsSummary, setAccountsSummary] = useState([]);
    const [selectedThird, setSelectedThird] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        fetchAccounts();
    }, [searchQuery]);

    async function fetchAccounts(forceReload = false) {
        setLoading(true);
        try {
            const result = await getReceivableSummary(
                profile.uid,
                profile.cid,
                1,
                100,
                searchQuery,
                forceReload
            );
            setAccountsSummary(result.data || []);

            if (forceReload) {
                dispatchCustomEvent("popup", {
                    message: "Cuentas actualizadas correctamente",
                    type: "success"
                });
            }
        } catch (err) {
            console.error("Error fetching receivable accounts:", err);
            dispatchCustomEvent("popup", {
                message: "Error al cargar cuentas por cobrar",
                type: "error"
            });
        } finally {
            setLoading(false);
        }
    }

    function handleSearchChange(e) {
        setSearchQuery(e.target.value);
        setSelectedThird(null);
        onSelect(null); // Limpiar selección al buscar
    }

    function handleThirdSelect(e) {
        const thirdId = parseInt(e.target.value);
        const third = accountsSummary.find(acc => acc.third_id === thirdId);
        setSelectedThird(third);

        // Si solo hay una cuenta, seleccionarla automáticamente
        if (third && third.details && third.details.length === 1) {
            handleAccountSelect(third.details[0]);
        } else {
            onSelect(null); // Limpiar selección de cuenta
        }
    }

    function handleAccountSelect(accountDetail) {
        onSelect({
            id: accountDetail.account_id,
            third_id: selectedThird?.third_id,
            third_name: selectedThird?.third_name,
            third_identification: selectedThird?.third_identification,
            current_balance: accountDetail.current_balance,
            initial_amount: accountDetail.initial_amount,
            sell_id: accountDetail.sell_id,
            bill_number: accountDetail.number,
            due_date: accountDetail.due_date,
            status: accountDetail.status,
            transactions: accountDetail.transactions || []
        });
    }

    // Calcular días de vencimiento
    function getDaysOverdue(dueDate) {
        if (!dueDate) return null;
        const today = new Date();
        const due = new Date(dueDate);
        const diffTime = today - due;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    }

    // Obtener badge de estado
    function getStatusBadge(dueDate) {
        const days = getDaysOverdue(dueDate);
        if (days === null) return <Badge bg="secondary" className="opacity-75">Sin vencimiento</Badge>;
        if (days < 0) return <Badge bg="success">Al día</Badge>;
        if (days === 0) return <Badge bg="warning" text="dark">Vence hoy</Badge>;
        if (days <= 30) return <Badge bg="warning" text="dark">Vencido {days}d</Badge>;
        return <Badge bg="danger">Vencido {days}d</Badge>;
    }

    return (
        <Col>
            <Form.Group as={Col} controlId="search-client" className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                    <Form.Label className="mb-0"><strong>Buscar Cliente</strong></Form.Label>
                    <div
                        className={`reload-icon-btn ${loading ? 'loading' : ''}`}
                        onClick={() => fetchAccounts(true)}
                        title="Actualizar cuentas por cobrar"
                    >
                        <RotateCw size={16} />
                    </div>
                </div>
                <Form.Control
                    type="text"
                    placeholder="Nombre o identificación del cliente"
                    onChange={handleSearchChange}
                    value={searchQuery}
                />
                <Form.Text className="text-muted">
                    Escribe para filtrar clientes con cuentas pendientes
                </Form.Text>
            </Form.Group>

            {accountsSummary.length > 0 && (
                <Form.Group as={Col} controlId="select-third" className="mb-3">
                    <Form.Label><strong>Seleccionar Cliente</strong></Form.Label>
                    <Form.Select
                        onChange={handleThirdSelect}
                        value={selectedThird?.third_id || ''}
                        disabled={loading}
                    >
                        <option value="">
                            {loading ? 'Cargando...' : 'Selecciona un cliente'}
                        </option>
                        {accountsSummary.map(third => (
                            <option key={third.third_id} value={third.third_id}>
                                {third.third_name} ({third.third_identification}) - {formatMoney(third.total_debt)} en {third.count_docs} documento(s)
                            </option>
                        ))}
                    </Form.Select>
                </Form.Group>
            )}

            {!loading && accountsSummary.length === 0 && (
                <div className="alert alert-info">
                    <i className="bi bi-info-circle me-2"></i>
                    {searchQuery ? 'No se encontraron clientes con ese criterio de búsqueda' : 'No hay cuentas por cobrar pendientes'}
                </div>
            )}

            {selectedThird && selectedThird.details && selectedThird.details.length > 0 && (
                <div className="mb-3">
                    <Form.Label className="text-dark fw-bold">Cuentas Pendientes</Form.Label>
                    <div className="receivable-table-container secondary">
                        <Table hover size="sm" className="mb-0 table-receivable-selector">
                            <thead>
                                <tr>
                                    <th>Factura</th>
                                    <th className="text-end">Monto Inicial</th>
                                    <th className="text-end">Saldo</th>
                                    <th>Estado</th>
                                    <th>Acción</th>
                                </tr>
                            </thead>
                            <tbody>
                                {selectedThird.details.map((detail) => (
                                    <tr
                                        key={detail.account_id}
                                        className={`receivable-account-row ${selectedAccountId === detail.account_id ? 'receivable-selected-account' : ''}`}
                                        onClick={() => handleAccountSelect(detail)}
                                    >
                                        <td>
                                            <div>
                                                <strong className="text-dark">#{detail.number}</strong>
                                                {detail.due_date && (
                                                    <div className="text-secondary small">
                                                        Vence: {new Date(detail.due_date).toLocaleDateString()}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="text-end text-dark">{formatMoney(detail.initial_amount)}</td>
                                        <td className="text-end">
                                            <strong className="text-danger">{formatMoney(detail.current_balance)}</strong>
                                        </td>
                                        <td>{getStatusBadge(detail.due_date)}</td>
                                        <td>
                                            <button
                                                type="button"
                                                className={`btn btn-sm ${selectedAccountId === detail.account_id ? 'btn-success' : 'btn-primary'}`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleAccountSelect(detail);
                                                }}
                                            >
                                                {selectedAccountId === detail.account_id ? 'Seleccionado' : 'Seleccionar'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>

                    {selectedThird.details.length > 1 && (
                        <div className="mt-2 p-2 receivable-info-alert">
                            <i className="bi bi-info-circle me-2"></i>
                            <strong>Total pendiente del cliente:</strong> {formatMoney(selectedThird.total_debt)} en {selectedThird.count_docs} documento(s)
                        </div>
                    )}
                </div>
            )}

            {error && (
                <div className="alert alert-danger mt-2">
                    {error}
                </div>
            )}
        </Col>
    );
}

export default ReceivableAccountSelector;
